
import React, { createContext, useContext, useEffect, useState, PropsWithChildren, useCallback, useRef } from 'react';
import { authService } from '../services/auth';
import { mapSessionUser, mapUser, logAuthStep } from '../services/auth_utils';
import { User, UserRole } from '../types';
import { useImpersonation } from '../hooks/useImpersonation';
import { supabase } from '../services/supabase';
import { GlobalLoader } from '../components/GlobalLoader';
import { db } from '../services/db'; // Added for pre-fetching config

// Structured Log Entry
export interface AuthLogEntry {
    id: string;
    timestamp: string;
    step: string;
    expected: string;
    received: string;
    status: 'success' | 'error' | 'warning' | 'info';
}

interface AuthContextType {
    user: User | null;
    session: any | null; 
    isLoading: boolean;
    isSessionSynced: boolean; // NY FLAGGA: Garanterar att token är satt i headers
    terminalError: string | null;
    impersonatingAdmin: User | null;
    handleImpersonate: (user: User) => void;
    handleLogout: () => Promise<void>;
    refreshUser: () => Promise<boolean>; 
    setManualUser: (user: User) => void;
    debugHistory: AuthLogEntry[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used within an AuthProvider");
    return context;
};

const STORAGE_KEY_USER = 'cn_opt_user';

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
    // OPTIMISTISK INITIERING: Läs från localStorage direkt
    const [currentUser, setCurrentUser] = useState<User | null>(() => {
        if (typeof window !== 'undefined') {
            try {
                const stored = localStorage.getItem(STORAGE_KEY_USER);
                return stored ? JSON.parse(stored) : null;
            } catch (e) { return null; }
        }
        return null;
    });

    const [currentSession, setCurrentSession] = useState<any | null>(null);
    const [isSessionSynced, setIsSessionSynced] = useState(false); // Default false, även med opt-user
    const [terminalError, setTerminalError] = useState<string | null>(null);
    
    // DETEKTION AV MAGIC LINK (Synkront)
    const [isProcessingHash, setIsProcessingHash] = useState(() => {
        if (typeof window === 'undefined') return false;
        const hash = window.location.hash;
        return hash.includes('access_token=') && hash.includes('refresh_token=');
    });

    // INIT LOADING STATE:
    // Om vi har en optimistisk användare (och inte håller på med hash-inloggning), är vi INTE initializing UI.
    const [isInitializing, setIsInitializing] = useState(() => {
        if (typeof window !== 'undefined' && window.location.hash.includes('access_token=')) return true;
        try {
            return !localStorage.getItem(STORAGE_KEY_USER);
        } catch { return true; }
    });
    
    const initStarted = useRef(false);

    // STRUCTURED DEBUG LOGGING
    const [debugHistory, setDebugHistory] = useState<AuthLogEntry[]>([]);
    
    const addLog = useCallback((step: string, expected: string, received: string, status: AuthLogEntry['status'] = 'info') => {
        const entry: AuthLogEntry = {
            id: Math.random().toString(36).substring(2, 9),
            timestamp: new Date().toLocaleTimeString(),
            step,
            expected,
            received,
            status
        };
        setDebugHistory(prev => [entry, ...prev].slice(0, 50));
    }, []);

    useEffect(() => {
        const handleAuthLog = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            if (detail) {
                addLog(detail.step, detail.expected, detail.received, detail.status);
            }
        };
        window.addEventListener('auth-log', handleAuthLog);
        return () => window.removeEventListener('auth-log', handleAuthLog);
    }, [addLog]);

    const { impersonatedUser, startImpersonating, stopImpersonating, isLoadingImpersonation } = useImpersonation();
    const syncChannel = useRef<BroadcastChannel | null>(null);

    const hydrateUser = useCallback(async (authUser: any, preloadedProfile?: any) => {
        logAuthStep("Hydrate User", "Profile Data", preloadedProfile ? "Preloaded Profile Found" : "Fetching from DB...", 'info');
        
        try {
            let profile = null;
            if (preloadedProfile) {
                profile = mapUser(preloadedProfile);
            } else {
                profile = await authService.fetchUserProfile(authUser.id);
            }
            
            if (!profile) {
                logAuthStep("Profile Fetch", "Existing Profile", "NULL - Attempting Repair", 'warning');
                profile = await authService.ensureUserProfile(authUser);
            }

            if (profile) {
                if (profile.status === 'DEACTIVATED' || profile.status === 'PENDING') {
                    logAuthStep("Status Check", "ACTIVE", profile.status, 'error');
                    await authService.signOut();
                    setCurrentUser(null);
                    localStorage.removeItem(STORAGE_KEY_USER); // Clear optimistic
                    setTerminalError(`ACCOUNT_${profile.status}`);
                } else {
                    // OPTIMERING: Jämför med vad vi redan har i localStorage för att undvika onödiga uppdateringar
                    const stored = localStorage.getItem(STORAGE_KEY_USER);
                    const newStr = JSON.stringify(profile);
                    
                    if (stored !== newStr) {
                        logAuthStep("Finalize", "User Object", `Updating User: ${profile.name}`, 'success');
                        setCurrentUser(profile);
                        localStorage.setItem(STORAGE_KEY_USER, newStr);
                    } else {
                        logAuthStep("Finalize", "User Object", `Data identical, skipping update`, 'info');
                    }
                }
            } else {
                // STRICT MODE: No Fallback
                logAuthStep("Hydrate Error", "Profile", "Profile not found in DB", 'error');
                throw new Error("Profil saknas i databasen. Kontakta support.");
            }
        } catch (e: any) {
            logAuthStep("Hydrate Exception", "Success", e.message, 'error');
            setCurrentUser(null);
            localStorage.removeItem(STORAGE_KEY_USER);
        }
    }, []);

    // CORE BOOT LOGIC (Standard Session Check)
    const lastSessionCheck = useRef(0);
    const performSessionCheck = useCallback(async () => {
        lastSessionCheck.current = Date.now();
        // logAuthStep("Session Check", "Cookie -> API", "Sending Request...", 'info'); 
        // Commented out to reduce spam in periodic checks
        
        try {
            const res = await fetch('/api/auth/session', { 
                method: 'GET',
                credentials: 'include',
                headers: { 'Cache-Control': 'no-cache' }
            });

            if (res.ok) {
                const data = await res.json();
                
                // const serverCookies = data.debug?.cookies_received_names?.join(', ') || 'NONE';
                // if (data.authenticated) {
                //      logAuthStep("Server Session", "sb-access-token", `Found: ${serverCookies}`, 'success');
                // } 

                if (data.authenticated && data.session) {
                    const { error: syncError } = await (supabase.auth as any).setSession(data.session);
                    
                    if (syncError) {
                        logAuthStep("Memory Sync", "Success", `Failed: ${syncError.message}`, 'warning');
                    } else {
                        // CRITICAL: Mark session as synced ONLY after setSession succeeds
                        // logAuthStep("Memory Sync", "Success", "Token set in Supabase Client", 'success');
                        setIsSessionSynced(true);
                    }

                    setCurrentSession(data.session);
                    await hydrateUser(data.user, data.profile);
                    return true;
                } else {
                    // SERVER SAID NO: CLEAR EVERYTHING
                    setCurrentUser(null);
                    setCurrentSession(null);
                    setIsSessionSynced(false);
                    localStorage.removeItem(STORAGE_KEY_USER);
                    return false;
                }
            } else {
                logAuthStep("API Network", "200 OK", `Status ${res.status}`, 'error');
            }
        } catch (e: any) {
            logAuthStep("Network Exception", "Response", e.message, 'error');
        }
        return false;
    }, [hydrateUser]);

    // INITIALIZATION EFFECT
    useEffect(() => {
        if (initStarted.current) return;
        initStarted.current = true;

        const bootApp = async () => {
            // --- SCENARIO 1: MAGIC LINK (HASH) DETECTED ---
            const hash = window.location.hash;
            if (hash && hash.includes('access_token=') && hash.includes('refresh_token=')) {
                logAuthStep("Boot", "Hash Detected", "Intercepting Magic Link...", 'info');
                setIsProcessingHash(true); // Ensure blocking is active
                setIsInitializing(true);   // Show spinner for magic link flow

                try {
                    const atMatch = hash.match(/access_token=([^&]+)/);
                    const rtMatch = hash.match(/refresh_token=([^&]+)/);

                    if (atMatch && rtMatch) {
                        // 1. Skicka tokens till servern för att sätta Cookies
                        const res = await fetch('/api/auth/session', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ 
                                access_token: atMatch[1],
                                refresh_token: rtMatch[1]
                            })
                        });

                        if (res.ok) {
                            logAuthStep("Magic Link", "Sync", "Success! Reloading...", 'success');
                            
                            const fullHash = window.location.hash;
                            const authStart = fullHash.search(/(?:[#?&])(access_token|error_description|refresh_token)=/);
                            
                            let targetRoute = '/';
                            if (authStart > -1) {
                                targetRoute = fullHash.substring(0, authStart);
                            } else {
                                targetRoute = fullHash;
                            }

                            if (!targetRoute || targetRoute === '#') {
                                window.history.replaceState(null, '', '/');
                            } else {
                                window.history.replaceState(null, '', targetRoute);
                            }
                            
                            window.location.reload();
                            return; 
                        } else {
                            logAuthStep("Magic Link", "Sync", "Failed (API Error)", 'error');
                        }
                    }
                } catch (e: any) {
                    logAuthStep("Magic Link", "Exception", e.message, 'error');
                }
                
                setIsProcessingHash(false); 
            }

            // --- SCENARIO 2: NORMAL BOOT (NO HASH) ---
            try {
                await performSessionCheck();
            } catch (e: any) {
                logAuthStep("Boot Error", "Session", e.message, 'error');
            } finally {
                setIsInitializing(false); 
            }
        };

        bootApp();

        // Keep-alive check
        const keepAliveInterval = setInterval(() => {
            if (document.visibilityState === 'visible' && !isProcessingHash) {
                performSessionCheck();
            }
        }, 45 * 60 * 1000);

        return () => clearInterval(keepAliveInterval);
    }, [performSessionCheck, isProcessingHash]);

    // Inloggningen gäller en timme. Har fliken legat i bakgrunden eller
    // datorn sovit har förnyelsen ovan hoppats över, så förnya direkt när
    // användaren kommer tillbaka.
    useEffect(() => {
        const onVisible = () => {
            if (!currentUser || document.visibilityState !== 'visible' || isProcessingHash) return;
            if (!lastSessionCheck.current || Date.now() - lastSessionCheck.current < 5 * 60 * 1000) return;
            performSessionCheck();
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, [currentUser, performSessionCheck, isProcessingHash]);

    // Handle Tab Sync
    useEffect(() => {
        try {
            syncChannel.current = new BroadcastChannel('cn_auth_sync');
            syncChannel.current.onmessage = (event) => {
                if (event.data === 'LOGIN') {
                    logAuthStep("Tab Sync", "Login Signal", "Reloading...", 'info');
                    window.location.reload();
                }
                if (event.data === 'LOGOUT') {
                    setCurrentUser(null);
                    setCurrentSession(null);
                    setIsSessionSynced(false);
                    localStorage.removeItem(STORAGE_KEY_USER);
                    window.location.href = '/';
                }
            };
        } catch (e) {}
        return () => syncChannel.current?.close();
    }, []);

    const handleLogout = async () => {
        if (impersonatedUser) {
            stopImpersonating();
            // Tillbaka till adminens översikt, inte den simulerade rollens flik.
            window.location.hash = '#/dashboard';
            return;
        }
        setIsInitializing(true);
        setIsSessionSynced(false); // Cut connection immediately
        logAuthStep("Logout", "Action", "Signing out...", 'info');
        localStorage.removeItem(STORAGE_KEY_USER); 
        
        try {
            try { await db.getSiteConfig('landing_page'); } catch (e) {}

            await authService.signOut(); 
            setCurrentUser(null);
            setCurrentSession(null);
            syncChannel.current?.postMessage('LOGOUT');
            window.location.href = '/'; 
        } catch (e: any) {
            window.location.reload();
        } finally {
            setIsInitializing(false);
        }
    };

    const handleImpersonate = (targetUser: User) => {
        if (currentUser?.role !== UserRole.ADMIN) return;
        startImpersonating(targetUser);
        // Börja på rollens egen översikt. Annars står man kvar på adminens
        // flik (t.ex. Användare med adminens filter), som rollen inte har.
        window.location.hash = '#/dashboard';
    };

    const setManualUser = (user: User) => {
        logAuthStep("Manual Set", "User Object", `User: ${user.email}`, 'success');
        setCurrentUser(user);
        localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user)); // Save for next reload
        setCurrentSession({ access_token: 'manual_override' });
        setIsSessionSynced(true); // We assume manual login handles token sync (via authService)
        setIsInitializing(false);
        syncChannel.current?.postMessage('LOGIN');
    };

    const refreshUser = async () => {
        return performSessionCheck();
    };

    const effectiveUser = impersonatedUser || currentUser;
    
    if (isProcessingHash) {
        return <GlobalLoader message="Verifierar och loggar in..." />;
    }

    const isGlobalLoading = isInitializing || isLoadingImpersonation;

    return (
        <AuthContext.Provider value={{ 
            user: effectiveUser, 
            session: currentSession, 
            isLoading: isGlobalLoading, 
            isSessionSynced,
            terminalError, 
            impersonatingAdmin: impersonatedUser, 
            handleImpersonate, 
            handleLogout, 
            refreshUser, 
            setManualUser, 
            debugHistory
        }}>
            {children}
        </AuthContext.Provider>
    );
};
